import { useState, useCallback, useMemo, useEffect } from 'react';
import { useChain } from '@cosmos-kit/react';
import { useQueryClient } from '@tanstack/react-query';
import { calculateFee } from '@cosmjs/stargate';
import { useEthWallet } from '@/hooks/useEthWallet';
import { useBridgeFees, usePendingDeposits, useInvalidateBridge } from '@/hooks/useBridge';
import { useGravityBalance } from '@/hooks/useGravityBalance';
import PendingSends from './PendingSends';
import {
  calculateChainFee,
  calculateMaxBridgeAmount,
  hasInsufficientBalance,
  gravToUgraviton,
  ugravitonToGrav,
  createSendToEthMsg,
  isValidEthAddress,
  isValidGravityAddress,
  shortenEthAddress,
  GRAVITY_BRIDGE_CONTRACT,
} from '@/services/bridge-messages';
import {
  checkBalance,
  executeDeposit,
  gravToWei,
  weiToGrav,
  EthTxState,
  GRAV_ERC20_ADDRESS,
  ETH_FINALITY_BLOCKS,
  ETH_BLOCK_TIME_SECS,
} from '@/services/eth-bridge';
import type { EthProvider } from '@/services/eth-bridge';
import {
  SelfRelayState,
  fetchBatchCalldata,
  submitBatchToEthereum,
  waitForBatchSignatures,
  findBatchNonceForUser,
} from '@/services/self-relay';
import { createRequestBatchMsg } from '@/services/bridge-messages';
import { BridgeDirection, BridgeSpeed } from '@/types/bridge';
import type { BridgeFeeTier } from '@/types/bridge';
import { text } from '@/config/theme';

/**
 * Status step component for ETH transaction progress
 */
interface StatusStepProps {
  label: string;
  isComplete: boolean;
  isActive: boolean;
  txHash?: string | null;
}

function StatusStep({ label, isComplete, isActive, txHash }: StatusStepProps): JSX.Element {
  return (
    <div className={`flex items-center space-x-3 ${isActive ? 'text-blue-400' : isComplete ? 'text-green-400' : 'text-gray-500'}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
        isComplete ? 'bg-green-600 border-green-600' : isActive ? 'border-blue-400' : 'border-gray-500'
      }`}>
        {isComplete && (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
        {isActive && !isComplete && (
          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
        )}
      </div>
      <div className="flex-1">
        <span className="font-medium">{label}</span>
        {txHash !== undefined && txHash !== null && (
          <a
            href={`https://etherscan.io/tx/${txHash}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm text-blue-400 hover:underline font-mono truncate"
          >
            {txHash.slice(0, 20)}...
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * Bridge page for transferring GRAV between Gravity Bridge and Ethereum
 */
export default function BridgePage(): JSX.Element {
  // Wallet connections
  const { address: cosmosAddress, isWalletConnected: isCosmosConnected, connect: connectCosmos, getSigningStargateClient } = useChain('gravitybridge');
  const ethWallet = useEthWallet();
  const queryClient = useQueryClient();

  // Form state
  const [direction, setDirection] = useState<BridgeDirection>(BridgeDirection.GRAVITY_TO_ETH);
  const [amount, setAmount] = useState('');
  const [selectedSpeed, setSelectedSpeed] = useState<BridgeSpeed>(BridgeSpeed.FAST);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ETH → Gravity specific state
  const [ethTxState, setEthTxState] = useState<EthTxState>(EthTxState.IDLE);
  const [ethTxHash, setEthTxHash] = useState<string | null>(null);
  const [ethBalanceWei, setEthBalanceWei] = useState<string>('0');

  // Self-relay specific state
  const [selfRelayState, setSelfRelayState] = useState<SelfRelayState>(SelfRelayState.IDLE);
  const [selfRelayBatchNonce, setSelfRelayBatchNonce] = useState<number | null>(null);
  const [selfRelayEthTxHash, setSelfRelayEthTxHash] = useState<string | null>(null);

  // Fetch bridge fees
  const amountUgraviton = useMemo(() => {
    const parsed = parseFloat(amount);
    return isNaN(parsed) || parsed <= 0 ? undefined : gravToUgraviton(amount);
  }, [amount]);
  const { data: bridgeFees } = useBridgeFees(amountUgraviton);
  const { invalidatePendingSends, invalidatePendingDeposits } = useInvalidateBridge();

  // Fetch Gravity Bridge balance
  const { data: gravityBalanceData } = useGravityBalance(cosmosAddress);

  // Fetch pending deposits for ETH → Gravity monitoring
  const { data: pendingDepositsData } = usePendingDeposits(
    direction === BridgeDirection.ETH_TO_GRAVITY ? cosmosAddress : undefined
  );

  // Fetch ETH GRAV balance when connected
  useEffect(() => {
    async function fetchEthBalance(): Promise<void> {
      if (!ethWallet.isConnected || ethWallet.address === null) {
        setEthBalanceWei('0');
        return;
      }
      try {
        const provider = window.ethereum as EthProvider | undefined;
        if (provider === undefined) return;
        const balance = await checkBalance(provider, ethWallet.address, GRAV_ERC20_ADDRESS);
        setEthBalanceWei(balance.toString());
      } catch (err) {
        console.error('Failed to fetch ETH GRAV balance:', err);
      }
    }
    void fetchEthBalance();
  }, [ethWallet.isConnected, ethWallet.address]);

  // Get the selected fee tier
  const selectedFeeTier = useMemo((): BridgeFeeTier | null => {
    if (bridgeFees === undefined) return null;
    switch (selectedSpeed) {
      case BridgeSpeed.SLOW:
        return bridgeFees.slow;
      case BridgeSpeed.FAST:
        return bridgeFees.fast;
      case BridgeSpeed.SELF_RELAY:
        return bridgeFees.self_relay;
    }
  }, [bridgeFees, selectedSpeed]);

  // Balance handling - use actual balance from chain when available
  const balanceUgraviton = gravityBalanceData ?? '0';
  const balanceGrav = ugravitonToGrav(balanceUgraviton, 2);
  const ethBalanceGrav = weiToGrav(ethBalanceWei, 4);

  // Calculate fees for display
  const bridgeFeeGrav = selectedFeeTier?.bridge_fee_ugraviton !== undefined && selectedFeeTier?.bridge_fee_ugraviton !== '' ? ugravitonToGrav(selectedFeeTier.bridge_fee_ugraviton, 4) : '0';
  const chainFeeGrav = amountUgraviton !== undefined ? ugravitonToGrav(calculateChainFee(amountUgraviton), 4) : '0';
  
  const totalFeeGrav = useMemo((): string => {
    if (selectedFeeTier?.bridge_fee_ugraviton === undefined || selectedFeeTier?.bridge_fee_ugraviton === '' || amountUgraviton === undefined) return '0';
    try {
      const bridgeFee = BigInt(selectedFeeTier.bridge_fee_ugraviton);
      const chainFee = BigInt(calculateChainFee(amountUgraviton));
      return ugravitonToGrav((bridgeFee + chainFee).toString(), 4);
    } catch {
      return '0';
    }
  }, [selectedFeeTier?.bridge_fee_ugraviton, amountUgraviton]);

  // Validation
  const isInsufficientBalance = useMemo((): boolean => {
    if (direction === BridgeDirection.GRAVITY_TO_ETH) {
      if (amountUgraviton === undefined || selectedFeeTier?.bridge_fee_ugraviton === undefined || selectedFeeTier?.bridge_fee_ugraviton === '') return false;
      return hasInsufficientBalance(balanceUgraviton, amountUgraviton, selectedFeeTier.bridge_fee_ugraviton);
    } else {
      // ETH → Gravity: check ETH GRAV balance
      const parsed = parseFloat(amount);
      if (isNaN(parsed) || parsed <= 0) return false;
      const amountWei = gravToWei(amount);
      const balanceWei = ethBalanceWei || '0';
      if (amountWei === '0' || balanceWei === '0') return false;
      try {
        return BigInt(balanceWei) < BigInt(amountWei);
      } catch {
        return false;
      }
    }
  }, [direction, amountUgraviton, selectedFeeTier, balanceUgraviton, amount, ethBalanceWei]);

  const ethDestAddress = ethWallet.address ?? '';
  const isValidEthDest = isValidEthAddress(ethDestAddress);
  const isValidGravityDest = cosmosAddress !== undefined && isValidGravityAddress(cosmosAddress);

  const canSubmit = useMemo((): boolean => {
    if (direction === BridgeDirection.GRAVITY_TO_ETH) {
      return (
        isCosmosConnected &&
        ethWallet.isConnected &&
        amountUgraviton !== undefined &&
        selectedFeeTier !== null &&
        selectedFeeTier?.bridge_fee_ugraviton !== undefined &&
        selectedFeeTier?.bridge_fee_ugraviton !== '' &&
        !isInsufficientBalance &&
        isValidEthDest &&
        !isSubmitting
      );
    } else {
      // ETH → Gravity
      const parsed = parseFloat(amount);
      return (
        isCosmosConnected &&
        ethWallet.isConnected &&
        !isNaN(parsed) &&
        parsed > 0 &&
        !isInsufficientBalance &&
        isValidGravityDest &&
        !isSubmitting &&
        ethTxState === EthTxState.IDLE
      );
    }
  }, [direction, isCosmosConnected, ethWallet.isConnected, amountUgraviton, selectedFeeTier, isInsufficientBalance, isValidEthDest, isValidGravityDest, isSubmitting, amount, ethTxState]);

  // Toggle direction
  const toggleDirection = useCallback(() => {
    setDirection(d => 
      d === BridgeDirection.GRAVITY_TO_ETH 
        ? BridgeDirection.ETH_TO_GRAVITY 
        : BridgeDirection.GRAVITY_TO_ETH
    );
    setAmount('');
    setError(null);
    setTxHash(null);
    setEthTxHash(null);
    setEthTxState(EthTxState.IDLE);
  }, []);

  // Set max amount
  const handleMaxAmount = useCallback((): void => {
    if (direction === BridgeDirection.GRAVITY_TO_ETH) {
      if (selectedFeeTier?.bridge_fee_ugraviton === undefined || selectedFeeTier?.bridge_fee_ugraviton === '') return;
      const maxAmount = calculateMaxBridgeAmount(balanceUgraviton, selectedFeeTier.bridge_fee_ugraviton);
      setAmount(ugravitonToGrav(maxAmount, 6));
    } else {
      // ETH → Gravity: use ETH balance
      setAmount(weiToGrav(ethBalanceWei, 6));
    }
  }, [direction, selectedFeeTier, balanceUgraviton, ethBalanceWei]);

  // Submit Gravity → ETH transaction
  const handleSubmitGravityToEth = useCallback(async (): Promise<void> => {
    if (!canSubmit || cosmosAddress === undefined || ethWallet.address === null || amountUgraviton === undefined || selectedFeeTier?.bridge_fee_ugraviton === undefined || selectedFeeTier?.bridge_fee_ugraviton === '') return;

    setIsSubmitting(true);
    setError(null);
    setTxHash(null);
    setSelfRelayState(SelfRelayState.IDLE);
    setSelfRelayBatchNonce(null);
    setSelfRelayEthTxHash(null);

    const isSelfRelay = selectedSpeed === BridgeSpeed.SELF_RELAY;

    try {
      const client = await getSigningStargateClient();
      
      const chainFee = calculateChainFee(amountUgraviton);
      
      // Step 1: Create and broadcast MsgSendToEth
      if (isSelfRelay) {
        setSelfRelayState(SelfRelayState.SEND_TO_ETH_PENDING);
      }
      
      const msg = createSendToEthMsg({
        sender: cosmosAddress,
        eth_dest: ethWallet.address,
        amount: amountUgraviton,
        bridge_fee: selectedFeeTier.bridge_fee_ugraviton,
        chain_fee: chainFee,
      });

      // Simulate gas
      const gasEstimate = await client.simulate(cosmosAddress, [msg], undefined);
      const gasLimit = Math.ceil(gasEstimate * 1.3);

      // Calculate fee with gas price
      const fee = calculateFee(gasLimit, '0.025ugraviton');

      // Sign and broadcast
      const result = await client.signAndBroadcast(
        cosmosAddress,
        [msg],
        fee,
        ''
      );

      if (result.code !== 0) {
        throw new Error(result.rawLog ?? 'Transaction failed');
      }

      setTxHash(result.transactionHash);
      
      // Invalidate caches
      void invalidatePendingSends(cosmosAddress);
      void queryClient.invalidateQueries({ queryKey: ['balance', 'gravity', cosmosAddress] });

      // If self-relay, continue with the relay flow
      if (isSelfRelay) {
        // Step 2: Request batch creation
        setSelfRelayState(SelfRelayState.REQUEST_BATCH_PENDING);
        
        const denom = 'ugraviton'; // TODO: support other denoms
        const requestBatchMsg = createRequestBatchMsg({
          sender: cosmosAddress,
          denom,
        });

        const batchGasEstimate = await client.simulate(cosmosAddress, [requestBatchMsg], undefined);
        const batchGasLimit = Math.ceil(batchGasEstimate * 1.3);
        const batchFee = calculateFee(batchGasLimit, '0.025ugraviton');

        const batchResult = await client.signAndBroadcast(
          cosmosAddress,
          [requestBatchMsg],
          batchFee,
          ''
        );

        if (batchResult.code !== 0) {
          throw new Error(batchResult.rawLog ?? 'Failed to request batch');
        }

        // Step 3: Find batch nonce for user
        setSelfRelayState(SelfRelayState.WAITING_FOR_SIGNATURES);
        const batchNonce = await findBatchNonceForUser(cosmosAddress);
        setSelfRelayBatchNonce(batchNonce);

        // Step 4: Wait for signatures
        await waitForBatchSignatures(batchNonce);

        // Step 5: Fetch batch calldata
        setSelfRelayState(SelfRelayState.FETCHING_CALLDATA);
        const calldataResponse = await fetchBatchCalldata(batchNonce);

        // Step 6: Submit to Ethereum
        setSelfRelayState(SelfRelayState.SUBMITTING_TO_ETH);
        const provider = window.ethereum as EthProvider | undefined;
        if (provider === undefined) {
          throw new Error('MetaMask not available');
        }

        const ethTxHashResult = await submitBatchToEthereum(provider, ethWallet.address, calldataResponse.calldata);
        setSelfRelayEthTxHash(ethTxHashResult);
        
        setSelfRelayState(SelfRelayState.ETH_TX_PENDING);

        // Step 7: Complete!
        setSelfRelayState(SelfRelayState.COMPLETE);
      }
      
      setAmount('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Transaction failed';
      setError(message);
      if (isSelfRelay) {
        setSelfRelayState(SelfRelayState.ERROR);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, cosmosAddress, ethWallet.address, amountUgraviton, selectedFeeTier, selectedSpeed, getSigningStargateClient, invalidatePendingSends, queryClient]);

  // Handle relay of an existing batch from PendingSends
  const handleRelayBatch = useCallback(async (batchNonce: number): Promise<void> => {
    if (ethWallet.address === null) {
      setError('Please connect your Ethereum wallet');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSelfRelayState(SelfRelayState.WAITING_FOR_SIGNATURES);
    setSelfRelayBatchNonce(batchNonce);
    setSelfRelayEthTxHash(null);

    try {
      // Step 1: Wait for batch signatures (or fetch calldata directly if ready)
      const calldataResponse = await waitForBatchSignatures(batchNonce);

      // Step 2: Submit to Ethereum
      setSelfRelayState(SelfRelayState.SUBMITTING_TO_ETH);
      const provider = window.ethereum as EthProvider | undefined;
      if (provider === undefined) {
        throw new Error('MetaMask not available');
      }

      const ethTxHashResult = await submitBatchToEthereum(provider, ethWallet.address, calldataResponse.calldata);
      setSelfRelayEthTxHash(ethTxHashResult);
      
      setSelfRelayState(SelfRelayState.ETH_TX_PENDING);

      // Step 3: Complete!
      setSelfRelayState(SelfRelayState.COMPLETE);
      
      // Refresh pending sends
      if (cosmosAddress !== undefined) {
        void queryClient.invalidateQueries({ queryKey: ['bridge', 'pending-sends-v2', cosmosAddress] });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Relay failed';
      setError(message);
      setSelfRelayState(SelfRelayState.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  }, [ethWallet.address, cosmosAddress, queryClient]);

  // State for tracking batch request
  const [isRequestingBatch, setIsRequestingBatch] = useState(false);

  // Handle manual request batch for pending (unbatched) transfers
  const handleRequestBatch = useCallback(async (): Promise<void> => {
    if (cosmosAddress === undefined) {
      setError('Please connect your Gravity Bridge wallet');
      return;
    }

    setIsRequestingBatch(true);
    setError(null);

    try {
      const client = await getSigningStargateClient();
      if (client === undefined) {
        throw new Error('Failed to get signing client');
      }

      const denom = 'ugraviton'; // TODO: support other denoms
      const requestBatchMsg = createRequestBatchMsg({
        sender: cosmosAddress,
        denom,
      });

      const gasEstimate = await client.simulate(cosmosAddress, [requestBatchMsg], undefined);
      const gasLimit = Math.ceil(gasEstimate * 1.3);
      const fee = calculateFee(gasLimit, '0.025ugraviton');

      const result = await client.signAndBroadcast(
        cosmosAddress,
        [requestBatchMsg],
        fee,
        ''
      );

      if (result.code !== 0) {
        throw new Error(result.rawLog ?? 'Failed to request batch');
      }

      // Refresh pending sends to show the new batch
      void queryClient.invalidateQueries({ queryKey: ['bridge', 'pending-sends-v2', cosmosAddress] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to request batch';
      setError(message);
    } finally {
      setIsRequestingBatch(false);
    }
  }, [cosmosAddress, getSigningStargateClient, queryClient]);


  // Submit ETH → Gravity transaction
  const handleSubmitEthToGravity = useCallback(async (): Promise<void> => {
    if (!canSubmit || cosmosAddress === undefined || ethWallet.address === null) return;

    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) return;

    setIsSubmitting(true);
    setError(null);
    setEthTxHash(null);
    setEthTxState(EthTxState.IDLE);

    try {
      const provider = window.ethereum as EthProvider | undefined;
      if (provider === undefined) {
        throw new Error('MetaMask not available');
      }

      const amountWei = gravToWei(amount);
      
      const result = await executeDeposit(
        provider,
        ethWallet.address,
        cosmosAddress,
        amountWei,
        (state, hash) => {
          setEthTxState(state);
          if (hash !== undefined) {
            setEthTxHash(hash);
          }
        }
      );

      if (result.success) {
        setEthTxState(EthTxState.WAITING_FINALITY);
        setAmount('');
        
        // Refresh ETH balance
        const newBalance = await checkBalance(provider, ethWallet.address, GRAV_ERC20_ADDRESS);
        setEthBalanceWei(newBalance.toString());
        
        // Invalidate caches - balance will update when deposit confirms
        void invalidatePendingDeposits(cosmosAddress);
        void queryClient.invalidateQueries({ queryKey: ['balance', 'gravity', cosmosAddress] });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Transaction failed';
      setError(message);
      setEthTxState(EthTxState.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, cosmosAddress, ethWallet.address, amount, invalidatePendingDeposits, queryClient]);

  // Combined submit handler
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (direction === BridgeDirection.GRAVITY_TO_ETH) {
      await handleSubmitGravityToEth();
    } else {
      await handleSubmitEthToGravity();
    }
  }, [direction, handleSubmitGravityToEth, handleSubmitEthToGravity]);

  // Wrapper for async button handlers
  const handleConnectCosmos = (): void => {
    void connectCosmos();
  };

  const handleConnectEth = (): void => {
    void ethWallet.connect();
  };

  const handleSubmitClick = (): void => {
    void handleSubmit();
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className={`text-3xl font-bold mb-2 ${text.primary}`}>Bridge</h1>
        <p className={`${text.secondary} mb-4`}>
          Bridge GRAV to and from Ethereum! More generic bridge interface coming soon
        </p>
      </div>

      {/* Wallet Connection Section */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Connect Wallets</h2>
        
        <div className="space-y-4">
          {/* Cosmos Wallet */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Gravity Bridge</p>
              {isCosmosConnected ? (
                <p className="font-mono text-sm">{cosmosAddress?.slice(0, 15)}...{cosmosAddress?.slice(-6)}</p>
              ) : (
                <p className="text-gray-500">Not connected</p>
              )}
            </div>
            <button
              onClick={handleConnectCosmos}
              className={`px-4 py-2 rounded-lg font-medium ${
                isCosmosConnected 
                  ? 'bg-green-600 text-white cursor-default' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              disabled={isCosmosConnected}
            >
              {isCosmosConnected ? '✓ Connected' : 'Connect Keplr'}
            </button>
          </div>

          {/* Ethereum Wallet */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Ethereum</p>
              {ethWallet.isConnected ? (
                <p className="font-mono text-sm">{shortenEthAddress(ethWallet.address ?? '')}</p>
              ) : (
                <p className="text-gray-500">Not connected</p>
              )}
            </div>
            {!ethWallet.isMetaMaskInstalled ? (
              <a
                href="https://metamask.io/download/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded-lg font-medium bg-orange-600 hover:bg-orange-700 text-white"
              >
                Install MetaMask
              </a>
            ) : (
              <button
                onClick={handleConnectEth}
                className={`px-4 py-2 rounded-lg font-medium ${
                  ethWallet.isConnected 
                    ? 'bg-green-600 text-white cursor-default' 
                    : 'bg-orange-600 hover:bg-orange-700 text-white'
                }`}
                disabled={ethWallet.isConnected || ethWallet.isConnecting}
              >
                {ethWallet.isConnecting ? 'Connecting...' : ethWallet.isConnected ? '✓ Connected' : 'Connect MetaMask'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Self-Relay Progress Indicator */}
      {direction === BridgeDirection.GRAVITY_TO_ETH && selfRelayState !== SelfRelayState.IDLE && (
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Self-Relay Progress</h3>
          <div className="space-y-3">
            {/* Step indicators */}
            <div className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                selfRelayState === SelfRelayState.WAITING_FOR_SIGNATURES ? 'bg-purple-500 animate-pulse' :
                [SelfRelayState.FETCHING_CALLDATA, SelfRelayState.SUBMITTING_TO_ETH, SelfRelayState.ETH_TX_PENDING, SelfRelayState.COMPLETE].includes(selfRelayState) ? 'bg-green-500' :
                'bg-gray-600'
              }`}>1</div>
              <span className={selfRelayState === SelfRelayState.WAITING_FOR_SIGNATURES ? 'text-purple-400' : 'text-gray-400'}>
                Waiting for validator signatures
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                [SelfRelayState.FETCHING_CALLDATA, SelfRelayState.SUBMITTING_TO_ETH].includes(selfRelayState) ? 'bg-purple-500 animate-pulse' :
                [SelfRelayState.ETH_TX_PENDING, SelfRelayState.COMPLETE].includes(selfRelayState) ? 'bg-green-500' :
                'bg-gray-600'
              }`}>2</div>
              <span className={[SelfRelayState.FETCHING_CALLDATA, SelfRelayState.SUBMITTING_TO_ETH].includes(selfRelayState) ? 'text-purple-400' : 'text-gray-400'}>
                Submit to Ethereum
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                selfRelayState === SelfRelayState.ETH_TX_PENDING ? 'bg-purple-500 animate-pulse' :
                selfRelayState === SelfRelayState.COMPLETE ? 'bg-green-500' :
                'bg-gray-600'
              }`}>3</div>
              <span className={selfRelayState === SelfRelayState.ETH_TX_PENDING ? 'text-purple-400' : 'text-gray-400'}>
                Confirm on Ethereum
              </span>
            </div>

            {/* Status message */}
            {selfRelayState === SelfRelayState.COMPLETE && (
              <div className="mt-4 p-3 bg-green-600/20 border border-green-500/30 rounded-lg">
                <p className="text-green-400">✓ Batch relayed successfully!</p>
                {selfRelayEthTxHash !== null && selfRelayEthTxHash !== undefined && selfRelayEthTxHash !== '' && (
                  <a
                    href={`https://etherscan.io/tx/${selfRelayEthTxHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-400 hover:underline"
                  >
                    View on Etherscan →
                  </a>
                )}
              </div>
            )}
            
            {selfRelayState === SelfRelayState.ERROR && error !== null && error !== undefined && error !== '' && (
              <div className="mt-4 p-3 bg-red-600/20 border border-red-500/30 rounded-lg">
                <p className="text-red-400">✗ Relay failed: {error}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending Sends Section - Show when Gravity → ETH direction */}
      {direction === BridgeDirection.GRAVITY_TO_ETH && (
        <PendingSends 
          gravityAddress={cosmosAddress}
          onRelayBatch={(batchNonce) => { void handleRelayBatch(batchNonce); }}
          onRequestBatch={() => { void handleRequestBatch(); }}
          isRelaying={isSubmitting && selfRelayState !== SelfRelayState.IDLE && selfRelayState !== SelfRelayState.ERROR && selfRelayState !== SelfRelayState.COMPLETE}
          relayingBatchNonce={selfRelayBatchNonce}
          isRequestingBatch={isRequestingBatch}
        />
      )}

      {/* Direction Toggle */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-center flex-1">
            <p className="text-sm text-gray-400 mb-1">From</p>
            <p className="font-semibold">
              {direction === BridgeDirection.GRAVITY_TO_ETH ? 'Gravity Bridge' : 'Ethereum'}
            </p>
          </div>
          
          <button
            onClick={toggleDirection}
            className="mx-4 p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
            title="Switch direction"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </button>
          
          <div className="text-center flex-1">
            <p className="text-sm text-gray-400 mb-1">To</p>
            <p className="font-semibold">
              {direction === BridgeDirection.GRAVITY_TO_ETH ? 'Ethereum' : 'Gravity Bridge'}
            </p>
          </div>
        </div>

        {direction === BridgeDirection.ETH_TO_GRAVITY && (
          <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-3 text-sm text-blue-200">
            <p className="font-medium mb-1">ETH → Gravity Bridge Transfer</p>
            <p>Deposits require ~{ETH_FINALITY_BLOCKS} block confirmations (~{Math.ceil(ETH_FINALITY_BLOCKS * ETH_BLOCK_TIME_SECS / 60)} minutes) for finality.</p>
          </div>
        )}
      </div>

      {/* ETH → Gravity Amount Input */}
      {direction === BridgeDirection.ETH_TO_GRAVITY && (
        <>
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-400">Amount</label>
              <p className="text-sm text-gray-400">
                ETH Balance: <span className="text-white">{ethBalanceGrav} GRAV</span>
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-lg focus:outline-none focus:border-blue-500"
                min="0"
                step="0.000001"
              />
              <span className="text-gray-400 font-medium">GRAV</span>
              <button
                onClick={handleMaxAmount}
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm font-medium"
              >
                MAX
              </button>
            </div>

            {isInsufficientBalance && (
              <p className="mt-2 text-sm text-red-400">Insufficient ETH GRAV balance</p>
            )}
          </div>

          {/* ETH → Gravity Summary */}
          {parseFloat(amount) > 0 && (
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Summary</h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Bridge Amount</span>
                  <span>{amount} GRAV</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Network Fees</span>
                  <span className="text-white">Paid in ETH (gas)</span>
                </div>
                <hr className="border-gray-700" />
                <div className="flex justify-between font-semibold">
                  <span>You will receive</span>
                  <span className="text-green-400">{amount} GRAV</span>
                </div>
              </div>

              {cosmosAddress !== undefined && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <p className="text-sm text-gray-400">Destination</p>
                  <p className="font-mono text-sm break-all">{cosmosAddress}</p>
                </div>
              )}
            </div>
          )}

          {/* Transaction Status */}
          {ethTxState !== EthTxState.IDLE && (
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Transaction Status</h3>
              
              <div className="space-y-3">
                <StatusStep
                  label="Checking Allowance"
                  isComplete={[EthTxState.APPROVING, EthTxState.APPROVAL_PENDING, EthTxState.SENDING, EthTxState.SEND_PENDING, EthTxState.WAITING_FINALITY, EthTxState.COMPLETE].includes(ethTxState)}
                  isActive={ethTxState === EthTxState.CHECKING_ALLOWANCE}
                />
                <StatusStep
                  label="Approving Token"
                  isComplete={[EthTxState.SENDING, EthTxState.SEND_PENDING, EthTxState.WAITING_FINALITY, EthTxState.COMPLETE].includes(ethTxState)}
                  isActive={ethTxState === EthTxState.APPROVING || ethTxState === EthTxState.APPROVAL_PENDING}
                  txHash={ethTxState === EthTxState.APPROVAL_PENDING ? ethTxHash : null}
                />
                <StatusStep
                  label="Sending to Cosmos"
                  isComplete={[EthTxState.WAITING_FINALITY, EthTxState.COMPLETE].includes(ethTxState)}
                  isActive={ethTxState === EthTxState.SENDING || ethTxState === EthTxState.SEND_PENDING}
                  txHash={ethTxState === EthTxState.SEND_PENDING ? ethTxHash : null}
                />
                <StatusStep
                  label={`Waiting for Finality (~${ETH_FINALITY_BLOCKS} blocks)`}
                  isComplete={ethTxState === EthTxState.COMPLETE}
                  isActive={ethTxState === EthTxState.WAITING_FINALITY}
                />
              </div>

              {ethTxHash !== null && ethTxState === EthTxState.WAITING_FINALITY && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <p className="text-sm text-gray-400 mb-1">Transaction Hash</p>
                  <a
                    href={`https://etherscan.io/tx/${ethTxHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-sm text-blue-400 hover:underline break-all"
                  >
                    {ethTxHash}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Pending Deposits */}
          {pendingDepositsData !== undefined && pendingDepositsData.deposits.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Pending Deposits</h3>
              
              <div className="space-y-4">
                {pendingDepositsData.deposits.map((deposit, index) => (
                  <div key={`${deposit.event_nonce}-${index}`} className="border border-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">{weiToGrav(deposit.amount, 4)} GRAV</span>
                      <span className={`text-sm px-2 py-1 rounded ${
                        deposit.confirmed 
                          ? 'bg-green-900 text-green-300' 
                          : 'bg-yellow-900 text-yellow-300'
                      }`}>
                        {deposit.confirmed ? 'Confirmed' : `${deposit.blocks_until_confirmed} blocks remaining`}
                      </span>
                    </div>
                    {!deposit.confirmed && (
                      <p className="text-sm text-gray-400">
                        ~{Math.ceil(deposit.seconds_until_confirmed / 60)} minutes remaining
                      </p>
                    )}
                    {deposit.tx_hash !== undefined && (
                      <a
                        href={`https://etherscan.io/tx/${deposit.tx_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-400 hover:underline"
                      >
                        View on Etherscan
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmitClick}
            disabled={!canSubmit}
            className={`w-full py-4 rounded-lg font-semibold text-lg transition-colors ${
              canSubmit
                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Processing...' : 'Bridge to Gravity'}
          </button>

          {/* Error Message */}
          {error !== null && (
            <div className="mt-4 p-4 bg-red-900/50 border border-red-600 rounded-lg">
              <p className="text-red-200">{error}</p>
            </div>
          )}
        </>
      )}

      {/* Amount Input */}
      {direction === BridgeDirection.GRAVITY_TO_ETH && (
        <>
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-400">Amount</label>
              <p className="text-sm text-gray-400">
                Balance: <span className="text-white">{balanceGrav} GRAV</span>
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-lg focus:outline-none focus:border-blue-500"
                min="0"
                step="0.000001"
              />
              <span className="text-gray-400 font-medium">GRAV</span>
              <button
                onClick={handleMaxAmount}
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm font-medium"
              >
                MAX
              </button>
            </div>

            {isInsufficientBalance && (
              <p className="mt-2 text-sm text-red-400">Insufficient balance</p>
            )}
          </div>

          {/* Fee Selection */}
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Bridge Speed</h3>
            
            <div className="space-y-3">
              {[BridgeSpeed.SLOW, BridgeSpeed.FAST, BridgeSpeed.SELF_RELAY].map((speed) => {
                const tier = speed === BridgeSpeed.SLOW 
                  ? bridgeFees?.slow 
                  : speed === BridgeSpeed.FAST 
                    ? bridgeFees?.fast 
                    : bridgeFees?.self_relay;
                
                const isSelfRelay = speed === BridgeSpeed.SELF_RELAY;
                
                return (
                  <label
                    key={speed}
                    className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedSpeed === speed
                        ? 'border-blue-500 bg-blue-900/20'
                        : 'border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="speed"
                        value={speed}
                        checked={selectedSpeed === speed}
                        onChange={() => setSelectedSpeed(speed)}
                        className="mr-3"
                      />
                      <div>
                        <p className="font-medium">{tier?.label ?? speed}</p>
                        <p className="text-sm text-gray-400">{tier?.estimated_time ?? ''}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {isSelfRelay ? (
                        <>
                          <p className="font-medium">
                            {tier !== undefined ? ugravitonToGrav(tier.bridge_fee_ugraviton, 4) : '...'} GRAV
                          </p>
                          <p className="text-sm text-gray-400">Bridge Fee</p>
                          <p className="text-sm text-yellow-400 mt-1">+ ETH gas</p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium">
                            {tier !== undefined ? ugravitonToGrav(tier.bridge_fee_ugraviton, 4) : '...'} GRAV
                          </p>
                          <p className="text-sm text-gray-400">Bridge Fee</p>
                        </>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Fee Summary */}
          {amountUgraviton !== undefined && selectedFeeTier !== null && (
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Summary</h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Bridge Amount</span>
                  <span>{amount} GRAV</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Bridge Fee</span>
                  <span>{bridgeFeeGrav} GRAV</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Chain Fee (0.2%)</span>
                  <span>{chainFeeGrav} GRAV</span>
                </div>
                <hr className="border-gray-700" />
                <div className="flex justify-between font-semibold">
                  <span>Total Fees</span>
                  <span>{totalFeeGrav} GRAV</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>You will receive</span>
                  <span className="text-white">{amount} GRAV</span>
                </div>
              </div>

              {ethWallet.address !== null && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <p className="text-sm text-gray-400">Destination</p>
                  <p className="font-mono text-sm break-all">{ethWallet.address}</p>
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmitClick}
            disabled={!canSubmit}
            className={`w-full py-4 rounded-lg font-semibold text-lg transition-colors ${
              canSubmit
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Submitting...' : 'Bridge to Ethereum'}
          </button>

          {/* Status Messages */}
          {error !== null && (
            <div className="mt-4 p-4 bg-red-900/50 border border-red-600 rounded-lg">
              <p className="text-red-200">{error}</p>
            </div>
          )}

          {txHash !== null && selfRelayState === SelfRelayState.IDLE && (
            <div className="mt-4 p-4 bg-green-900/50 border border-green-600 rounded-lg">
              <p className="text-green-200 mb-2">Transaction submitted successfully!</p>
              <p className="text-sm text-gray-300 font-mono break-all">{txHash}</p>
            </div>
          )}
        </>
      )}

      {/* Contract Info */}
      <div className="mt-8 text-center text-sm text-gray-500">
        <p>Gravity Bridge Contract:</p>
        <a
          href={`https://etherscan.io/address/${GRAVITY_BRIDGE_CONTRACT}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-blue-400 hover:underline"
        >
          {shortenEthAddress(GRAVITY_BRIDGE_CONTRACT)}
        </a>
      </div>
    </div>
  );
}
